/**
 * Add / edit Family Handover Vault item modal.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  CATEGORY_LABELS,
  HANDOVER_VAULT_CATEGORIES,
  HANDOVER_VAULT_PASSWORD_WARNING,
  HANDOVER_VAULT_SECTION_ORDER,
} from '../../content/handoverVaultContent';

const CONTACT_CATEGORIES = new Set(['important_contact', 'notify_person']);
const DOCUMENT_CATEGORIES = new Set(['document_location']);

export default function HandoverVaultItemModal({
  open,
  onOpenChange,
  initialCategory = 'important_contact',
  editingItem = null,
  onSave,
  saving = false,
}) {
  const [category, setCategory] = useState(initialCategory);
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactRelationship, setContactRelationship] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [documentLocation, setDocumentLocation] = useState('');
  const [reminderDate, setReminderDate] = useState('');

  useEffect(() => {
    if (!open) return;
    if (editingItem) {
      setCategory(editingItem.category || initialCategory);
      setTitle(editingItem.title || '');
      setNotes(editingItem.description || '');
      setContactName(editingItem.contact_name || '');
      setContactRelationship(editingItem.contact_relationship || '');
      setContactEmail(editingItem.contact_email || '');
      setContactPhone(editingItem.contact_phone || '');
      setOrganisation(editingItem.organisation || '');
      setDocumentType(editingItem.document_type || '');
      setDocumentLocation(editingItem.document_location || '');
      setReminderDate(editingItem.reminder_date || '');
    } else {
      setCategory(initialCategory);
      setTitle('');
      setNotes('');
      setContactName('');
      setContactRelationship('');
      setContactEmail('');
      setContactPhone('');
      setOrganisation('');
      setDocumentType('');
      setDocumentLocation('');
      setReminderDate('');
    }
  }, [open, editingItem, initialCategory]);

  const sectionMeta = HANDOVER_VAULT_CATEGORIES[category];
  const showContactFields = CONTACT_CATEGORIES.has(category);
  const showDocumentFields = DOCUMENT_CATEGORIES.has(category);
  const showPasswordWarning = category === 'account_instruction';
  const showReminderDate = category === 'review_reminder';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    onSave({
      category,
      title: title.trim(),
      description: notes.trim() || null,
      contact_name: showContactFields ? contactName.trim() || null : null,
      contact_relationship: showContactFields ? contactRelationship.trim() || null : null,
      contact_email: showContactFields ? contactEmail.trim() || null : null,
      contact_phone: showContactFields ? contactPhone.trim() || null : null,
      organisation: showContactFields ? organisation.trim() || null : null,
      document_type: showDocumentFields ? documentType.trim() || null : null,
      document_location: showDocumentFields ? documentLocation.trim() || null : documentLocation.trim() || null,
      reminder_date: showReminderDate ? reminderDate || null : null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#e8e2d8] bg-[#fffcf7] sm:max-w-lg" data-testid="handover-vault-item-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-normal text-[#1c1917]">
            {editingItem ? 'Edit vault item' : sectionMeta?.addLabel || 'Add vault item'}
          </DialogTitle>
          <DialogDescription className="text-[#5c534a]">
            Private to you. This is not legal advice.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="vault-category">Category</Label>
            <Select value={category} onValueChange={setCategory} disabled={Boolean(editingItem)}>
              <SelectTrigger id="vault-category" data-testid="vault-item-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HANDOVER_VAULT_SECTION_ORDER.map((key) => (
                  <SelectItem key={key} value={key}>
                    {CATEGORY_LABELS[key]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="vault-title">Title</Label>
            <Input
              id="vault-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Short label your family will recognise"
              required
              data-testid="vault-item-title"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="vault-notes">Notes</Label>
            <Textarea
              id="vault-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Practical details — keep it calm and clear"
              rows={4}
              data-testid="vault-item-notes"
            />
          </div>

          {showPasswordWarning ? (
            <p className="rounded-lg border border-amber-200/80 bg-amber-50/80 px-3 py-2 text-xs leading-relaxed text-amber-900">
              {HANDOVER_VAULT_PASSWORD_WARNING}
            </p>
          ) : null}

          {showContactFields ? (
            <div className="space-y-3 rounded-lg border border-[#e8e2d8] bg-white/60 p-3">
              <p className="text-xs font-medium text-[#5c534a]">Contact details (optional)</p>
              <Input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Name" />
              <Input
                value={contactRelationship}
                onChange={(e) => setContactRelationship(e.target.value)}
                placeholder="Relationship or role"
              />
              <Input value={organisation} onChange={(e) => setOrganisation(e.target.value)} placeholder="Organisation" />
              <Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="Email" />
              <Input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="Phone" />
            </div>
          ) : null}

          {showDocumentFields ? (
            <div className="space-y-3 rounded-lg border border-[#e8e2d8] bg-white/60 p-3">
              <p className="text-xs font-medium text-[#5c534a]">Document details (optional)</p>
              <Input value={documentType} onChange={(e) => setDocumentType(e.target.value)} placeholder="Document type (e.g. will, insurance)" />
              <Input
                value={documentLocation}
                onChange={(e) => setDocumentLocation(e.target.value)}
                placeholder="Where it is stored (safe, folder, solicitor)"
              />
            </div>
          ) : null}

          {showReminderDate ? (
            <div className="space-y-2">
              <Label htmlFor="vault-reminder-date">Review date (optional)</Label>
              <Input
                id="vault-reminder-date"
                type="date"
                value={reminderDate}
                onChange={(e) => setReminderDate(e.target.value)}
              />
              <p className="text-xs text-[#7a7268]">
                For full reminder emails and notifications, use{' '}
                <a href="/reminders" className="underline hover:text-[#5c534a]">
                  Reminders
                </a>
                .
              </p>
            </div>
          ) : null}

          <DialogFooter className="gap-2 sm:justify-end">
            <Button type="button" variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" className="rounded-full bg-[#8B7355] hover:bg-[#7A6548]" disabled={saving || !title.trim()} data-testid="vault-item-save">
              {saving ? 'Saving…' : editingItem ? 'Save changes' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
